// BoatTrader-only scope (TRIDENT-999): YachtWorld/Boats.com listing URL support
// was explicitly descoped by the PO. Do not add other hostnames here without
// re-confirming scope.
const ALLOWED_BOAT_URL_HOSTNAMES = new Set(['www.boattrader.com', 'www.qa.boattrader.com']);

// Matches a trailing digit run at the end of the last path segment, either
// hyphen-delimited (the "9426090" in "2024-sea-ray-slx-260-9426090" from
// https://www.boattrader.com/boat/2024-sea-ray-slx-260-9426090/) or the
// entire segment on its own (the "6000016" in the id-only shape
// https://www.boattrader.com/boat/6000016/ — no slug at all).
const PATH_IMT_ID_PATTERN = /(?:^|-)(\d+)$/;

// Real BoatTrader IMT ids observed across this codebase's fixtures/tests are
// consistently 6-7+ digits (e.g. 9426090, 6152141, 8789843). A short trailing
// digit run is far more likely to be part of a model designation (Sea Ray SLX
// "260", Boston Whaler "320") than a real id — codex-review caught a case
// where a "-260" model-number suffix was mistaken for the id, shadowing a
// genuine id supplied via the imtId query param on the very same URL shape
// this function's own docstring documents. Require a minimum digit-run length
// before trusting the path-embedded match at all.
const MIN_PATH_IMT_ID_DIGITS = 5;

/**
 * Extracts the IMT id from a BoatTrader listing URL (production or QA
 * domain only).
 *
 * Supports three URL shapes:
 * 1. Path-embedded (slugged): the numeric id is the trailing hyphen-delimited
 *    segment of the last path component (with or without a trailing slash),
 *    e.g. `/boat/2024-sea-ray-slx-260-9426090/` or `/boat/...-9426090?utm=...`.
 * 2. Path-embedded (bare id, no slug): the last path component is the id on
 *    its own, e.g. `/boat/6000016/`.
 * 3. Query-param: an explicit `imtId` query parameter, e.g.
 *    `/boat/2024-sea-ray-slx-260/?imtId=9426090`.
 *
 * The path-embedded shape takes precedence when both are present.
 *
 * Returns undefined (never throws) for: invalid/unparseable URLs, any
 * off-domain hostname (including YachtWorld/Boats.com — descoped per PO),
 * and any BoatTrader URL with no extractable numeric id in either shape.
 */
export function extractImtIdFromBoatUrl(url: string | null | undefined): string | undefined {
  if (!url || typeof url !== 'string') {
    return undefined;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (error) {
    return undefined;
  }

  if (!ALLOWED_BOAT_URL_HOSTNAMES.has(parsed.hostname.toLowerCase())) {
    return undefined;
  }

  const pathSegments = parsed.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  if (lastSegment) {
    const pathMatch = lastSegment.match(PATH_IMT_ID_PATTERN);
    if (pathMatch && pathMatch[1].length >= MIN_PATH_IMT_ID_DIGITS) {
      return pathMatch[1];
    }
  }

  const queryImtId = parsed.searchParams.get('imtId');
  if (queryImtId && /^\d+$/.test(queryImtId)) {
    return queryImtId;
  }

  return undefined;
}
